import { createContext, ReactElement, ReactNode, useCallback, useContext } from 'react'

import { PeriodMode } from '../../../../../enums/periodMode'
import { SortDirection } from '../../../../../enums/sortDirection'
import { useQueryParams } from '../../../../../hooks/useQueryParams'
import { EmberEventFn } from '../../../../../types/emberEventFn'
import { BankMatchesSortProperty } from '../enums/bankMatchesSortProperty'
import { BankMatchesType } from '../enums/bankMatchesType'

interface BankMatchesToolbarFilters {
  period: string
  sortProperty: BankMatchesSortProperty
  sortDirection: SortDirection
  transactionType?: BankMatchesType
  searchQuery?: string
}

const defaultQuery: BankMatchesToolbarFilters = {
  period: PeriodMode.All,
  sortProperty: BankMatchesSortProperty.EntryDate,
  sortDirection: SortDirection.Desc,
  transactionType: undefined,
  searchQuery: undefined,
}

interface BankMatchesToolbarFiltersContextState {
  queryParams: BankMatchesToolbarFilters
  setQueryParams: (query: Partial<BankMatchesToolbarFilters>) => void
}

const BankMatchesToolbarFiltersContext = createContext<BankMatchesToolbarFiltersContextState | undefined>(undefined)

interface BankMatchesToolbarFiltersProviderProps {
  children: ReactNode
  onFiltersChange: EmberEventFn
}

export const BankMatchesToolbarFiltersContextProvider = ({
  children,
  onFiltersChange,
}: BankMatchesToolbarFiltersProviderProps): ReactElement => {
  const [queryParams, setQueryParams] = useQueryParams<BankMatchesToolbarFilters>({
    defaultQuery,
  })

  const handleSetQueryParams = useCallback(
    (partialFilters: Partial<BankMatchesToolbarFilters>) => {
      const updatedFilters = { ...queryParams, ...partialFilters }

      setQueryParams(partialFilters)
      onFiltersChange({ detail: { filters: updatedFilters } })
    },
    [onFiltersChange, queryParams, setQueryParams],
  )

  return (
    <BankMatchesToolbarFiltersContext.Provider value={{ queryParams, setQueryParams: handleSetQueryParams }}>
      {children}
    </BankMatchesToolbarFiltersContext.Provider>
  )
}

export const useBankMatchesToolbarFilters = () => {
  const context = useContext(BankMatchesToolbarFiltersContext)

  if (!context) {
    throw new Error('BankMatchesToolbarFiltersContextProvider is missing in the module!')
  }

  return context
}
